import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  CalendarIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const AboutPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const yearsActive = currentYear - 2014;

  const highlights = [
    {
      icon: CalendarIcon,
      title: 'Founded in 2014',
      description: 'Over a decade of dedicated service to Nigeria\'s cybersecurity community',
    },
    {
      icon: UserGroupIcon,
      title: 'Multi-Stakeholder',
      description: 'Members from government, private sector, academia, and military',
    },
    {
      icon: GlobeAltIcon,
      title: 'National Reach',
      description: 'Active chapters across all geopolitical zones in Nigeria',
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Non-Profit Organization',
      description: 'Dedicated to advancing cybersecurity excellence across Nigeria',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gray-900 section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              About CSEAN
            </h1>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-200 leading-relaxed">
              Nigeria's leading umbrella body of cybersecurity professionals. Together, we champion cybersecurity advocacy nationally, contributing to national cybersecurity policy and strategy development.
            </p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Founded in January 2014, CSEAN is a non-profit association with professional members across Nigeria and the diaspora. Our membership spans government, private sector, academia, and the military, enabling us to bring a multi-stakeholder perspective to national cybersecurity challenges.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We champion cybersecurity advocacy nationally, contributing to national cybersecurity policy and strategy development, frameworks, and innovations that protect Nigeria's evolving digital economy and reinforce its national cyber resilience architecture.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Highlights Section */}
      <div className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((highlight, index) => (
              <div key={index} className="card text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-lg mb-4">
                  <highlight.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {highlight.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
            </div>
            <div className="bg-primary-50 rounded-xl p-8 mb-8">
              <p className="text-xl text-gray-800 leading-relaxed text-center font-medium">
                To build a global resourceful cybersecurity organisation leveraged on securing, positively transforming and enriching our digital experiences, socially and economically.
              </p>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              Our mandate is simple yet nationally impactful: to strengthen Nigeria's resilience against cyber threats while building capacity for the future of a secure digital economy.
            </p>
          </div>
        </div>
      </div>

      {/* Executive Leadership Section */}
      <div className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Executive Leadership
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Meet the dedicated leaders driving CSEAN's mission to strengthen Nigeria's cybersecurity landscape
            </p>
            <Link
              to="/about/executives"
              className="btn-primary inline-flex items-center"
            >
              View Executive Team
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>

      {/* Key Facts Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Key Facts
              </h2>
              <div className="w-24 h-1 bg-primary-500 mx-auto"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="card">
                <div className="flex items-start">
                  <ShieldCheckIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {yearsActive}+ Years of Excellence
                    </h3>
                    <p className="text-gray-600">
                      Since 2014, CSEAN has been at the forefront of cybersecurity advocacy and capacity building in Nigeria.
                    </p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-start">
                  <UserGroupIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Diverse Membership
                    </h3>
                    <p className="text-gray-600">
                      Our members represent government, private sector, academia, and military, bringing diverse perspectives to cybersecurity challenges.
                    </p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-start">
                  <GlobeAltIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      National Presence
                    </h3>
                    <p className="text-gray-600">
                      Active chapters across all geopolitical zones, engaging professionals and students nationwide.
                    </p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-start">
                  <BuildingOfficeIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Policy Impact
                    </h3>
                    <p className="text-gray-600">
                      Contributing to National Cybersecurity Policy, Cybercrime Prohibition Act, and multiple national committees.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;

